import { authFetch } from "@/lib/api-client";

// ─── Types ───────────────────────────────────────────────────────────

export interface PromptVersionDto {
  id: string;
  version: number;
  content: string;
  /** Null = service default at call sites. */
  model: string | null;
  createdBy: string | null;
  createdAt: string;
  variables: string[];
}

export interface PromptLlmModelDto {
  id: string;
  label: string;
  provider: "anthropic" | "google";
  family: "claude" | "gemini";
  tier: "economy" | "standard" | "premium";
  costHint: "$" | "$$" | "$$$";
  capabilities: Array<"text" | "vision" | "image">;
  /** Listed by Vertex but not invokable by this project — shown locked. */
  unavailable?: boolean;
}

export interface PromptLlmModelsResponseDto {
  models: PromptLlmModelDto[];
  fetchedAt: string | null;
  fromBootstrap: boolean;
}

export type PromptTaskTypeDto = "TEXT" | "VISION" | "IMAGE";

export interface PromptDto {
  id: string;
  name: string;
  usedIn: string;
  status: string;
  taskType: PromptTaskTypeDto;
  tenantId: string | null;
  tenantName: string | null;
  createdAt: string;
  updatedAt: string;
  latestVersion: PromptVersionDto | null;
}

export interface PromptsListFilters {
  usedIn?: string;
  tenantId?: string;
  status?: string;
  taskType?: PromptTaskTypeDto;
}

// ─── API functions ───────────────────────────────────────────────────

export async function fetchPrompts(filters: PromptsListFilters = {}): Promise<PromptDto[]> {
  const params = new URLSearchParams();
  if (filters.usedIn) params.set("usedIn", filters.usedIn);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.status) params.set("status", filters.status);
  if (filters.taskType) params.set("taskType", filters.taskType);

  const qs = params.toString();
  const url = `/prompts${qs ? `?${qs}` : ""}`;
  const { data } = await authFetch<PromptDto[]>(url);
  return data;
}

export async function fetchPrompt(id: string): Promise<PromptDto> {
  const { data } = await authFetch<PromptDto>(`/prompts/${id}`);
  return data;
}

export async function fetchPromptVersions(promptId: string): Promise<PromptVersionDto[]> {
  const { data } = await authFetch<PromptVersionDto[]>(`/prompts/${promptId}/versions`);
  return data;
}

export interface CreatePromptResult {
  prompt: PromptDto;
  globalFallbackCreated: boolean;
}

export async function createPrompt(body: {
  name: string;
  usedIn: string;
  tenantId?: string | null;
  content: string;
  model?: string | null;
  taskType?: PromptTaskTypeDto;
  variables?: string[];
}): Promise<CreatePromptResult> {
  const res = await authFetch("/prompts", {
    method: "POST",
    body: JSON.stringify(body),
    rawResponse: true,
  });
  const json = (await res.json()) as { data: PromptDto; globalFallbackCreated?: boolean };
  return { prompt: json.data, globalFallbackCreated: json.globalFallbackCreated ?? false };
}

export async function updatePrompt(id: string, body: {
  name?: string;
  status?: string;
  taskType?: PromptTaskTypeDto;
}): Promise<PromptDto> {
  const { data } = await authFetch<PromptDto>(`/prompts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return data;
}

/** Soft delete — the row is hidden from every list but stays recoverable. */
export async function deletePrompt(id: string): Promise<PromptDto> {
  const { data } = await authFetch<PromptDto>(`/prompts/${id}`, {
    method: "DELETE",
  });
  return data;
}

export async function createPromptVersion(promptId: string, body: {
  content: string;
  model?: string | null;
  /** Sent with the version so taskType+model validate and persist atomically. */
  taskType?: PromptTaskTypeDto;
  variables?: string[];
}): Promise<PromptVersionDto> {
  const { data } = await authFetch<PromptVersionDto>(`/prompts/${promptId}/versions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}

export async function revertPromptVersion(promptId: string, versionId: string): Promise<PromptVersionDto> {
  const { data } = await authFetch<PromptVersionDto>(`/prompts/${promptId}/revert/${versionId}`, {
    method: "POST",
  });
  return data;
}

export async function fetchUsedInValues(): Promise<string[]> {
  const { data } = await authFetch<string[]>("/prompts/used-in-values");
  return data;
}

export async function fetchVariablesByUsedIn(usedIn: string): Promise<string[]> {
  const { data } = await authFetch<string[]>(`/prompts/variables/${encodeURIComponent(usedIn)}`);
  return data;
}

export async function fetchPromptLlmModels(): Promise<PromptLlmModelsResponseDto> {
  const { data } = await authFetch<PromptLlmModelsResponseDto>("/prompts/llm-models");
  return data;
}

export async function refreshPromptLlmModels(): Promise<PromptLlmModelsResponseDto> {
  const { data } = await authFetch<PromptLlmModelsResponseDto>("/prompts/llm-models/refresh", {
    method: "POST",
  });
  return data;
}
